import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Button, Card, message, Modal, Descriptions, Tag, Input } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { CustomTable } from "@/hocComponents";
import http from "@/utils/http";
import api from "@/utils/api";
import commonFn from "@/utils/commonFn";

export default function PropertyBreakdown({
  supervisorId,
  supervisorName,
  ownerRecord,
  visible,
  onClose,
}) {
  const [propertyData, setPropertyData] = useState([]);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [tableHead, setTableHead] = useState({});
  const [searchValue, setSearchValue] = useState("");
  const debounceTimerRef = useRef(null);
  const requestIdRef = useRef(0);
  const lastSearchValueRef = useRef(""); // 记录上一次搜索的值

  const displaySupervisorName =
    supervisorName || ownerRecord?.supervisorName || ownerRecord?.supervisor || "";

  const formatAmount = (value) => commonFn.amountToDollarBracketFn(value || 0);

  const transformPropertyRows = (data = {}) => {
    const rows = [];
    
    (data.current || []).forEach((item, index) => {
      rows.push({
        ...item,
        key: `current-${item.propertyId || index}`,
        rowType: 'current',
        rowLabel: 'Total Current Tenant Delinquency',
        current: formatAmount(item.current),
        days30: formatAmount(item.days30),
        days60: formatAmount(item.days60),
        days90Plus: formatAmount(item.days90Plus),
        total: formatAmount(item.total),
      });
    });
    
    (data.eviction || []).forEach((item, index) => {
      rows.push({
        ...item,
        key: `eviction-${item.propertyId || index}`,
        rowType: 'eviction',
        rowLabel: 'Total Current In Eviction Delinquency',
        current: formatAmount(item.current),
        days30: formatAmount(item.days30),
        days60: formatAmount(item.days60),
        days90Plus: formatAmount(item.days90Plus),
        total: formatAmount(item.total),
      });
    });

    return rows;
  };

  // 初始加载数据
  const loadInitialData = useCallback(async () => {
    if (!supervisorId || supervisorId === "__ALL__") {
      setPropertyData([]);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    setPropertyLoading(true);
    try {
      const response = await http.get(api.getDelinquencyByProp, {
        params: { supervisorId },
      });

      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      if (response?.code === "1") {
        setPropertyData(transformPropertyRows(response.data || {}));
      } else {
        setPropertyData([]);
        message.error(response?.msg);
      }
    } catch (e) {
      console.error(e);
      if (e?.name !== 'CanceledError' && e?.name !== 'AbortError') {
        setPropertyData([]);
        message.error("Request error");
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setPropertyLoading(false);
      }
    }
  }, [supervisorId]);

  // 搜索函数
  const fetchPropertyDetail = useCallback((propName = "") => {
    // 清除定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // 空值：重置为初始数据
    if (!propName || propName.trim() === "") {
      setSearchValue("");
      lastSearchValueRef.current = "";
      loadInitialData();
      return;
    }

    // 如果和上一次搜索的值相同，不重复请求
    const trimmedValue = propName.trim();
    if (lastSearchValueRef.current === trimmedValue) {
      return;
    }
    lastSearchValueRef.current = trimmedValue;

    // 防抖延迟
    debounceTimerRef.current = setTimeout(() => {
      if (!supervisorId || supervisorId === "__ALL__") {
        setPropertyData([]);
        return;
      }

      const currentRequestId = ++requestIdRef.current;
      setPropertyLoading(true);

      http.get(api.getDelinquencyByPropDetail, {
        params: { supervisorId, propName: trimmedValue },
      })
        .then((response) => {
          if (currentRequestId !== requestIdRef.current) {
            return;
          }
          if (response?.code === "1") {
            const data = response.data || {};
            setPropertyData(transformPropertyRows(data));
          } else {
            setPropertyData([]);
            message.error(response?.msg);
          }
        })
        .catch((e) => {
          console.error(e);
          if (e?.name !== 'CanceledError' && e?.name !== 'AbortError') {
            setPropertyData([]);
            message.error("Request error");
          }
        })
        .finally(() => {
          if (currentRequestId === requestIdRef.current) {
            setPropertyLoading(false);
          }
        });
    }, 300); // 降低到 300ms，体验更好
  }, [supervisorId, loadInitialData]);

  // 搜索框变化处理
  const handleSearchChange = useCallback((e) => {
    const value = e?.target?.value || "";
    setSearchValue(value);
    setTableHead((prev) => ({
      ...prev,
      propertyName: value,
    }));
    fetchPropertyDetail(value);
  }, [fetchPropertyDetail]);

  const propertyColumns = useMemo(
    () => [
      {
        title: "Type",
        dataIndex: "rowLabel",
        width: 220,
        render: (text) => <span>{text}</span>,
      },
      {
        title: "Property Name",
        dataIndex: "propertyName",
        width: 320,
        titleType: "Input",
        onChange: handleSearchChange,
        render: (text) => <span>{text}</span>,
      },
      {
        title: "Prop Type",
        dataIndex: "propType",
        width: 100,
      },
      {
        title: "Units",
        dataIndex: "units",
        width: 90,
        render: (value) => Number(value || 0).toLocaleString(),
      },
      {
        title: "Current(<30)",
        dataIndex: "current",
        width: 130,
      },
      {
        title: "30 Days",
        dataIndex: "days30",
        width: 120,
      },
      {
        title: "60 Days",
        dataIndex: "days60",
        width: 120,
      },
      {
        title: "90+ Days",
        dataIndex: "days90Plus",
        width: 120,
      },
      {
        title: "Total",
        dataIndex: "total",
        width: 140,
      },
    ],
    [handleSearchChange],
  );

  useEffect(() => {
    if (visible) {
      setTableHead({});
      setSearchValue("");
      lastSearchValueRef.current = "";
      requestIdRef.current = 0;
      loadInitialData();
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [visible, supervisorId, loadInitialData]);

  if (!visible) return null;

  return (
    <div className="property-bottom-panel property-bottom-panel-open">
      <Button
        type="default"
        shape="circle"
        icon={<DownOutlined />}
        className="property-collapse-btn"
        onClick={onClose}
      />

      <Card className="section-card property-card">
        <div className="section-card-header-property">
          <div>
            <div className="section-card-title">Property Detail</div>
            {displaySupervisorName && (
              <div className="property-modal-meta">
                <span className="property-modal-meta-owner">
                  {displaySupervisorName}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="section-card-table">
          <CustomTable
            tableLoading={propertyLoading}
            tableList={propertyData}
            columns={propertyColumns}
            pagination={false}
            tableScrollY="360px"
            setTableHead={setTableHead}
            getTableListFn={loadInitialData}
          />
        </div>
      </Card>
    </div>
  );
}